import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../data.js';
import { ASSETS } from '../assetManifest.js';

interface Props {
  onLoadingComplete: () => void;
}

export function LoadingScreen({ onLoadingComplete }: Props) {
  const [loadingText, setLoadingText] = useState(CONSTANTS.LOADING.LOADING_TEXTS[0]);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const duration = CONSTANTS.LOADING.DURATION_MS;
    const textInterval = duration / CONSTANTS.LOADING.LOADING_TEXTS.length;
    
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / (duration / 50)); // Update every 50ms
        if (newProgress >= 100) {
          clearInterval(progressInterval);
          setTimeout(onLoadingComplete, 200); // Small delay after reaching 100%
          return 100;
        }
        return newProgress;
      });
    }, 50);

    // Text rotation
    let textIndex = 0;
    const textRotationInterval = setInterval(() => {
      textIndex = (textIndex + 1) % CONSTANTS.LOADING.LOADING_TEXTS.length;
      setLoadingText(CONSTANTS.LOADING.LOADING_TEXTS[textIndex]);
    }, textInterval);

    return () => {
      clearInterval(progressInterval);
      clearInterval(textRotationInterval);
    };
  }, [onLoadingComplete]);

  // Validate spinner asset
  const spinnerCfg = (ASSETS as any)?.ui?.loading_spinner;
  if (!spinnerCfg || !spinnerCfg.path || typeof spinnerCfg.path !== 'string') {
    throw new Error('ASSETS.ui.loading_spinner.path is missing or invalid. Define a valid path in src/assetManifest.js');
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white font-sans">
      <div className="text-center">
        {/* Spinning loading icon */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 360 / CONSTANTS.LOADING.SPINNER_ROTATION_SPEED,
            repeat: Infinity,
            ease: "linear"
          }}
          className="mx-auto mb-8"
        >
          <img
            src={spinnerCfg.path}
            alt="Loading"
            width={spinnerCfg.width || 64}
            height={spinnerCfg.height || 64}
            className="mx-auto"
          />
        </motion.div>

        {/* Loading text */}
        <motion.p
          key={loadingText}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="text-xl text-slate-300 mb-8"
        >
          {loadingText}
        </motion.p>

        {/* Progress bar */}
        <div className="w-80 mx-auto">
          <div className="bg-slate-700 rounded-full h-2 mb-4">
            <motion.div
              className="bg-gradient-to-r from-amber-400 to-orange-400 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
          <p className="text-sm text-slate-400">
            {Math.round(progress)}%
          </p>
        </div>
      </div>
    </div>
  );
}
